import { differenceInDays, parseISO, isValid } from 'date-fns';
import type { Job } from '@/data/jobs';

/**
 * Determines if a job is urgent based on its closing date
 * A job is considered urgent if it closes within 7 days
 */
export const isJobUrgent = (closingDate?: string): boolean => {
  if (!closingDate) return false;
  
  try {
    const closing = parseISO(closingDate);
    if (!isValid(closing)) return false;
    
    const today = new Date();
    const daysUntilClosing = differenceInDays(closing, today);
    
    // Urgent if closing within 7 days and hasn't passed
    return daysUntilClosing >= 0 && daysUntilClosing <= 7;
  } catch {
    return false;
  }
};

export const hasUrgentStatus = (job: Pick<Job, 'closingDate' | 'urgent'>): boolean => {
  // Expired jobs are never urgent — they're presented as ongoing
  if (isJobExpired(job.closingDate)) return false;
  // Otherwise urgent if explicitly flagged or closing within 7 days
  return Boolean(job.urgent) || isJobUrgent(job.closingDate);
};



/**
 * Formats a closing date for display
 */
export const formatClosingDate = (closingDate?: string): string => {
  if (!closingDate) return '';
  
  try {
    const closing = parseISO(closingDate);
    if (!isValid(closing)) return closingDate;
    
    return closing.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return closingDate;
  }
};

/**
 * Gets the number of days until a job closes
 */
export const getDaysUntilClosing = (closingDate?: string): number | null => {
  if (!closingDate) return null;
  
  try {
    const closing = parseISO(closingDate);
    if (!isValid(closing)) return null;
    
    const today = new Date();
    return differenceInDays(closing, today);
  } catch {
    return null;
  }
};

/**
 * Determines if a job is expired (closing date has passed)
 */
export const isJobExpired = (closingDate?: string): boolean => {
  if (!closingDate) return false;
  
  try {
    const closing = parseISO(closingDate);
    if (!isValid(closing)) return false;
    
    const today = new Date();
    const daysUntilClosing = differenceInDays(closing, today);
    
    // Expired if closing date has passed (negative days)
    return daysUntilClosing < 0;
  } catch {
    return false;
  }
};

/**
 * Determines if a job was recently added (posted within the last 7 days)
 * Uses closingDate - 30 days as the posted date, matching the structured data logic
 */
export const isRecentlyAdded = (closingDate?: string): boolean => {
  if (!closingDate) return false;
  
  try {
    const closing = parseISO(closingDate);
    if (!isValid(closing)) return false;
    
    const postedDate = new Date(closing);
    postedDate.setDate(postedDate.getDate() - 30);
    
    const today = new Date();
    const daysSincePosted = differenceInDays(today, postedDate);
    
    return daysSincePosted >= 0 && daysSincePosted <= 7;
  } catch {
    return false;
  }
};
